import { toRendererCoordinates } from './previewFrameGeometry';
import { clampToPositionRange, toBaseUnits } from './canvasElementSnapping';
import {
    calculateMaximumSizeFactor,
    calculateMinimumSizeFactor
} from '../../components/VideoElementsEditor/ImageElements/imageElementSizeCalculations';

// Size factors are floats, so a click that moved nothing still lands a hair away from where it
// started. Committing that would cost the user an undo step that appears to do nothing.
const SIZE_FACTOR_TOLERANCE = 0.0001;

// Resizes image elements by their corner squares. Only an image element is ever resized: text and the
// visualizer are dragged alone, so a target that says it cannot be resized is left to the drag
// controller and the squares are not even drawn around it.
//
// The picture keeps its own proportions throughout:
// one factor scales both axes, exactly as the Width and Height controls in the settings panel do,
// and the two therefore always agree on how much of the frame the picture covers.
//
// The corner diagonally opposite the one being dragged stays where it is, so the picture grows away
// from it the way it does in a drawing application. That means a resize moves the picture as well as
// scaling it: the size factor alone cannot express "this corner did not move". Both are written to
// the store in one action on release, so the whole gesture is still a single undo step.
//
// Everything here is in base units, the same 500-tall space a stored position is written in, so the
// anchored corner is worked out in the units it will be stored in and no conversion sits in between.
export default class ImageElementResizeController {

    constructor(canvas, { highlight, getAspectRatio, getMaxPosition }) {
        this.canvas = canvas;
        this.highlight = highlight;
        this.getAspectRatio = getAspectRatio;
        this.getMaxPosition = getMaxPosition;

        this.resizeState = null;

        // Kept so destroy() can take them off again. The outline owns these elements and removes them
        // with itself, but this controller must not depend on being destroyed first to stop listening.
        this.handleListeners = this.highlight.getHandles().map(handle => {
            const onPointerDown = (event) => this.handlePointerDown(event, handle);
            handle.element.addEventListener('pointerdown', onPointerDown);

            return { element: handle.element, onPointerDown: onPointerDown };
        });

        // The pointer is captured by the square it was put down on, but the window is listened to
        // anyway: a browser that refuses the capture would otherwise drop the resize the moment the
        // pointer left a square ten pixels wide.
        window.addEventListener('pointermove', this.handlePointerMove);
        window.addEventListener('pointerup', this.handlePointerUp);
        window.addEventListener('pointercancel', this.handlePointerUp);
    }

    handlePointerDown(event, handle) {
        const target = this.highlight.getTarget();

        // The squares are hidden around anything that cannot be resized, so this is belt and braces
        // against a pointer that went down on one just as the outline moved onto a line of text.
        if (!target || !target.canResize)
            return;

        const pictureAtGrab = target.measureBox();

        if (!pictureAtGrab)
            return;

        const sizeFactorAtGrab = target.getSizeFactor();
        const positionAtGrab = { x: pictureAtGrab.centerX, y: pictureAtGrab.centerY };

        this.resizeState = {
            pointerId: event.pointerId,
            handleElement: handle.element,
            corner: handle.corner,
            target: target,
            pictureAtGrab: pictureAtGrab,
            anchor: this.measureAnchoredCorner(pictureAtGrab, handle.corner),
            sizeFactorAtGrab: sizeFactorAtGrab,
            positionAtGrab: positionAtGrab,
            current: { sizeFactor: sizeFactorAtGrab, position: positionAtGrab }
        };

        // Keeps the picture the drag started on under the outline even if the pointer wanders over
        // another element on the way.
        this.highlight.lockTo(target);

        // Without this the browser starts its own selection drag and the gesture is lost.
        event.preventDefault();

        // Capture keeps the resize alive when the pointer leaves the square; it is not essential,
        // and a pointer the browser no longer knows about makes it throw.
        try { handle.element.setPointerCapture(event.pointerId); } catch { /* the window listeners carry it */ }
    }

    handlePointerMove = (event) => {
        if (!this.resizeState || event.pointerId !== this.resizeState.pointerId)
            return;

        const { pictureAtGrab, corner, anchor, sizeFactorAtGrab, target } = this.resizeState;

        // The pointer is taken to be holding the corner itself, not the spot inside the square it
        // actually went down on: the corner snaps under the cursor on the first move and stays
        // there for the whole drag. Carrying the grab offset instead read as the corner drifting
        // away from the hand, because the offset stayed in the drag while the picture grew (bug 4844).
        const scale = this.measureScaleTowardsCorner(this.toBaseUnits(event), pictureAtGrab, anchor, corner);

        const sizeFactor = this.clampToAnchoredPositionRange(
            this.clampToAllowedRange(sizeFactorAtGrab * scale, target)
        );

        // Applied within the event on purpose. Browsers already deliver pointer moves at most once
        // per paint, and pointer events run before the animation frame callbacks of the same paint,
        // which is where PIXI renders. A resize deferred to its own animation frame therefore landed
        // after the render and reached the screen a frame late, with the picture visibly trailing
        // both the pointer and the outline for the whole gesture (bug 4844).
        this.applyResize({
            sizeFactor: sizeFactor,
            position: this.positionForSizeFactor(sizeFactor)
        });
    };

    // Where the picture has to sit for the anchored corner to stay put at this size. The picture's
    // half-size grows in step with the factor, so the middle moves out from the anchor by exactly the
    // same proportion, and the opposite corner lands back where the pointer left it.
    positionForSizeFactor(sizeFactor) {
        const { pictureAtGrab, anchor, corner, sizeFactorAtGrab } = this.resizeState;
        const scale = sizeFactorAtGrab === 0 ? 1 : sizeFactor / sizeFactorAtGrab;

        return this.clampToSliderRange({
            x: anchor.x + corner.signX * pictureAtGrab.halfWidth * scale,
            y: anchor.y + corner.signY * pictureAtGrab.halfHeight * scale
        });
    }

    // The corner that stays put: the one across the picture from the square being dragged.
    measureAnchoredCorner(picture, corner) {
        return {
            x: picture.centerX - corner.signX * picture.halfWidth,
            y: picture.centerY - corner.signY * picture.halfHeight
        };
    }

    applyResize(resize) {
        const { current, target } = this.resizeState;

        if (this.isSameResize(resize, current))
            return;

        this.resizeState.current = resize;

        target.resizeTo(resize.sizeFactor);
        target.moveTo(resize.position);
        this.highlight.showAround(target);
    }

    isSameResize(one, other) {
        return Math.abs(one.sizeFactor - other.sizeFactor) <= SIZE_FACTOR_TOLERANCE
            && one.position.x === other.position.x
            && one.position.y === other.position.y;
    }

    handlePointerUp = (event) => {
        if (!this.resizeState || event.pointerId !== this.resizeState.pointerId)
            return;

        const { target, handleElement, sizeFactorAtGrab, positionAtGrab, current } = this.resizeState;
        const resized = !this.isSameResize(current, { sizeFactor: sizeFactorAtGrab, position: positionAtGrab });

        this.resizeState = null;
        try { handleElement.releasePointerCapture(event.pointerId); } catch { /* capture was never taken */ }

        this.highlight.unlock();
        this.highlight.showAround(target);

        // One action carrying both, so a single undo puts the picture back at the size AND the place
        // it was before the gesture.
        if (resized)
            target.commitResize(current.sizeFactor, current.position);
    };

    // How far the pointer has reached out from the anchored corner, as a share of the picture's own
    // span on each axis, with the further axis deciding: 1 is the dragged corner where the grab left
    // it, 2 is twice the picture away from the anchor, 0 is back at the anchor.
    //
    // The further axis wins because the picture keeps its own proportions, so the corner can only
    // ever slide along the diagonal. Projecting the pointer onto that diagonal moved the corner at
    // only half the hand's speed on a straight sideways pull, and the cursor ran far ahead of the
    // square. Following the axis being pulled keeps the corner exactly under the pointer there,
    // while the other axis grows in proportion, so the hand never leaves the picture.
    measureScaleTowardsCorner(pointer, picture, anchor, corner) {
        const reachAcrossWidth = (pointer.x - anchor.x) * corner.signX / (2 * picture.halfWidth);
        const reachAcrossHeight = (pointer.y - anchor.y) * corner.signY / (2 * picture.halfHeight);

        return Math.max(reachAcrossWidth, reachAcrossHeight);
    }

    // The same floor and ceiling the Width and Height sliders stop at, so a picture cannot be dragged
    // to a size the settings panel would refuse to show.
    clampToAllowedRange(sizeFactor, target) {
        const naturalSize = target.getNaturalSize();

        if (!naturalSize)
            return sizeFactor;

        const aspectRatio = this.getAspectRatio();
        const minimum = calculateMinimumSizeFactor(naturalSize, aspectRatio);
        const maximum = calculateMaximumSizeFactor(naturalSize, aspectRatio);

        return Math.min(Math.max(sizeFactor, minimum), maximum);
    }

    // The picture's middle moves out from the anchor in step with the size, so the range the
    // Position sliders can express turns into a size range: past it, clampToSliderRange would pull
    // the middle back into range and the anchored corner would slide along with it in the middle of
    // the gesture. The size is stopped here instead, and the anchor stays put however far the
    // pointer goes.
    clampToAnchoredPositionRange(sizeFactor) {
        const { anchor, corner, pictureAtGrab, sizeFactorAtGrab } = this.resizeState;

        if (sizeFactorAtGrab === 0)
            return sizeFactor;

        const { maxXPosition, maxYPosition } = this.getMaxPosition();
        const acrossWidth = this.sizeFactorRangeForAxis(anchor.x, corner.signX * pictureAtGrab.halfWidth, maxXPosition);
        const acrossHeight = this.sizeFactorRangeForAxis(anchor.y, corner.signY * pictureAtGrab.halfHeight, maxYPosition);

        const minimum = Math.max(acrossWidth.minimum, acrossHeight.minimum);
        const maximum = Math.min(acrossWidth.maximum, acrossHeight.maximum);

        // A picture that already sits outside the sliders' range has no size that brings it back
        // with the anchor kept; the gesture then runs as it always has, clamped by position.
        if (minimum > maximum)
            return sizeFactor;

        return Math.min(Math.max(sizeFactor, minimum), maximum);
    }

    // The sizes at which the picture's middle stays within ±limit on one axis. The middle sits one
    // step from the anchor at the grab size and moves in proportion to the size, so the two ends of
    // the position range map straight onto the two ends of a size range.
    sizeFactorRangeForAxis(anchorCoordinate, stepFromAnchor, limit) {
        if (!Number.isFinite(limit))
            return { minimum: -Infinity, maximum: Infinity };

        const { sizeFactorAtGrab } = this.resizeState;
        const atOneEnd = (-limit - anchorCoordinate) / stepFromAnchor * sizeFactorAtGrab;
        const atOtherEnd = (limit - anchorCoordinate) / stepFromAnchor * sizeFactorAtGrab;

        return {
            minimum: Math.min(atOneEnd, atOtherEnd),
            maximum: Math.max(atOneEnd, atOtherEnd)
        };
    }

    clampToSliderRange(position) {
        return clampToPositionRange(position, this.getMaxPosition());
    }

    toBaseUnits(event) {
        return toBaseUnits(toRendererCoordinates(this.canvas, event));
    }

    destroy() {
        window.removeEventListener('pointermove', this.handlePointerMove);
        window.removeEventListener('pointerup', this.handlePointerUp);
        window.removeEventListener('pointercancel', this.handlePointerUp);

        this.handleListeners.forEach(({ element, onPointerDown }) =>
            element.removeEventListener('pointerdown', onPointerDown));
        this.handleListeners = [];

        this.resizeState = null;
        this.highlight.unlock();
    }
}
